import { useEffect, useRef, useState } from 'react';

import { mutate } from 'swr';

import { FeedItem, normalizeThreadNodes, THREAD_NODE_KIND } from '@/lib/api';

/** Maximum number of polling attempts before giving up */
const MAX_ATTEMPTS = 4;

interface UseFeedSummaryPollingOptions {
  feedItems: FeedItem[];
  feedRequestTarget: string;
  enabled: boolean;
}

/** Check if any items need summaries (2+ readable docs, no summary yet) */
function hasMissingSummaries(items: FeedItem[]): boolean {
  // Backend skips summary generation for single-post threads
  return items.some(
    (item) =>
      item != null &&
      !item.summary &&
      normalizeThreadNodes(item.thread?.nodes).filter(
        (node) => node.kind === THREAD_NODE_KIND.Document
      ).length >= 2
  );
}

/**
 * Polls the feed request target with exponential backoff if any items lack summaries.
 * Stops when all items have summaries or after MAX_ATTEMPTS.
 */
export function useFeedSummaryPolling({
  feedItems,
  feedRequestTarget,
  enabled,
}: UseFeedSummaryPollingOptions) {
  const [attempts, setAttempts] = useState<number>(0);
  const lastFeedRequestTargetRef = useRef<string>(feedRequestTarget);

  // Reset attempts when feed request target changes (user loaded more items or switched feed type)
  useEffect(() => {
    if (feedRequestTarget !== lastFeedRequestTargetRef.current) {
      setAttempts(0);
      lastFeedRequestTargetRef.current = feedRequestTarget;
    }
  }, [feedRequestTarget]);

  useEffect(() => {
    if (!enabled) return;
    if (!feedItems || feedItems.length === 0) return;
    if (!hasMissingSummaries(feedItems)) return;
    if (attempts >= MAX_ATTEMPTS) return;

    // Exponential backoff: 4s, 8s, 16s, 32s
    const delay = 2 ** (attempts + 2) * 1000;

    // If the component unmounts while mutate() is pending, setAttempts will fire
    // on an unmounted component. This is low risk since the Home component
    // typically stays mounted for the page lifetime. If this becomes an issue,
    // an isMounted ref pattern could be added to guard the state update.
    const timer = setTimeout(async () => {
      try {
        await mutate(feedRequestTarget);
      } catch (error) {
        console.warn('Failed to revalidate feed data:', error);
      }
      setAttempts((a) => a + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [feedItems, feedRequestTarget, enabled, attempts]);
}
